import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
:host {
    height: 80%;
}
.inventory-component {
    height: 100%;
}

.inventory {
    height: 100%;
    background: rgba(41, 35, 35, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    gap: 12px;
    padding: 20px;
    overflow: auto;
    overflow-x: hidden;
}
.inventory h3 {
    justify-self: center;
    margin-top: 10px;
}

.inventory-items-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
}

.inventory-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px;
    text-align: center;
    color: #fff;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.inventory-item:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
}

.inventory-item img {
    width: 100%;
    height: auto;
    max-width: 80px;
    image-rendering: pixelated;
    border-radius: 6px;
    transition: opacity 0.2s ease-in-out;
}

.no-items {
    text-align: center;
    color: #fff;
    font-size: 1.2rem;
    font-weight: bold;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    animation: fadeIn 0.5s ease-in-out;
}

.inventory-item .name {
    margin-top: 5px;
    font-size: 1rem;
    font-weight: bold;
    color: #f5f5f5;
}

.inventory::-webkit-scrollbar {
    width: 8px;
}

.inventory::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

.inventory::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
}

.inventory::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}
`;

/**
 * Represents the Inventory page
 */
export class InventoryComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _items: Array<{ name: string; images: string[] }> = [];

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.render();
    }

    /**
    * Render the contents of this page
    */
    public async render(): Promise<void> {
        await this.loadInventory();

        const element: HTMLElement = this.renderInventory();

        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(element);
    }

    /**
     * Load inventory items from the game service
     */
    private async loadInventory(): Promise<void> {
        try {
            const inventory: {
                items: {
                    name: string;
                    images: string[];
                }[];
            } = await this._gameRouteService.getInventory();
            this._items = inventory.items;
        }
        catch (error) {
            console.error("Failed to load inventory:", error);
        }
    }

    /**
     * Gives the player a message to let them know they can accuse the murderer
     * @returns a html element containing a sentence if you have collected enough items or it returns undefined
     */
    private accuseMessage(): HTMLElement | undefined {
        if (this._items.length > 4) {
            const sufficientInventoryCount: HTMLElement = html`<h4>When ready go to the ballroom to accuse!</h4>`;
            if (this._items.length === 5) {
                for (let i: number = 0; i < 8; i++) {
                    setTimeout(() => {
                        sufficientInventoryCount.style.color = i % 2 === 0 ? "red" : "white";
                        if (i === 7) {
                            setTimeout(() => {
                                sufficientInventoryCount.style.color = "white";
                            }, 400);
                        }
                    }, i * 400);
                }
            }
            return sufficientInventoryCount;
        }
        else {
            return html`<div></div>`;
        }
    }

    /**
     * Render the inventory container
     */
    private renderInventory(): HTMLElement {
        return html`
        <div class="inventory-component">
            <style>${styles}</style>
            <div class="inventory">
                <h3>Inventory</h3>
                ${this.accuseMessage()}
                <div class="inventory-items-container">
                    ${this._items.length === 0 ? this.renderNoItems() : this.renderItems()}
                </div>
            </div>
        </div>`;
    }

    /**
     * Render the inventory items
     */
    private renderItems(): HTMLElement[] {
        return htmlArray`
            ${this._items.map(item => html`
                <div class="inventory-item">
                    ${this.renderImage(item.images).outerHTML}
                    <div class="name">${item.name}</div>
                </div>
            `)}
        `;
    }

    /**
     * Render images for an inventory item, with a fallback image
     */
    private renderImage(images: string[]): HTMLElement {
        const imgSrc: string = images.length > 0 ? images[0] : "assets/img/rooms/itemDefault.png";
        return html`
            <img src="assets/img/rooms/${imgSrc}.png" onerror="this.src='assets/img/rooms/itemDefault.png'"/>
        `;
    }

    /**
     * Render message when inventory is empty
     */
    private renderNoItems(): HTMLElement {
        return html`<div class="no-items">No items in your inventory</div>`;
    }
}
